import type {
  AccountModel,
  LoginParams,
  LoginTypesResponse,
  MFAActivationRequirements,
  MFAActivationUnauhthorizedModel,
  OTPLoginParams,
  PasswordChange,
  PasswordReset,
} from '/#/models/accountModel'
import type { OrganizationBasicModel } from '/#/models/organizationModel'
import type { ComputedRef } from 'vue'

import { ACCEPT_LANGUAGE_KEY, CSRF_TOKEN_KEY } from '/@/constants/common'
import { router } from '/@/router'
import { store } from '/@/store'
import { decode, encode } from '/@/utils/encryption-tools'
import { defHttp } from '/@/utils/http'

export const VALID_OTP_DURATION_IN_SECONDS = 180

const useImpersonatingOrReleasingProgressState = createGlobalState(() =>
  useStorage('impersonating-releasing', false),
)

const usePreviousUserInfo = createGlobalState(() => useStorage('previous-session', ''))

export const useAccountStore = defineStore('account', () => {
  const user = ref<Nullable<AccountModel>>()
  const { configs } = useConfigStore()
  const { isAnyAnalystManager } = useUserPermission()

  const token = useStorage('csrf-token', '')
  const loginEmail = useStorage('login-email', '', sessionStorage)
  const rememberedEmail = useStorage('remembered-email', '', localStorage)
  const loginTypes = useStorage<Nullable<LoginTypesResponse>>(
    'login-types',
    null,
    sessionStorage,
  )

  // This holds the language used for API requests, currently not connected to i18n.
  const userLanguage = useStorage('language', 'en')

  const csrfToken = ref<string>('')
  const loading = ref<boolean>(true)
  const isLoggedIn = useStorage('logged-in', false)

  const isLoginProgressContinue = ref(false)
  const isImpersonatingOrReleasingProgressContinue =
    useImpersonatingOrReleasingProgressState()
  const previousSessionUser = usePreviousUserInfo()

  const isMFARequired = ref<boolean>(false)
  const isMFAActivationEnforced = ref<boolean | undefined>(false)

  const MFAActivationUID = useStorage('maid', '')
  const MFAActivationToken = useStorage('mat', '')
  const MFAActivationQRCodeURI = useStorage('maqcu', '')

  const isImpersonated: ComputedRef<boolean> = computed(
    () => !!user.value?.is_impersonated,
  )

  const isMultiLanguage: ComputedRef<boolean> = computed(
    () => !!user.value?.organization?.is_multi_language,
  )

  /**
   * Logged-in user organization.
   */
  const accountOrganization: ComputedRef<OrganizationBasicModel | undefined | null> =
    computed(() => {
      return user.value?.organization
    })

  /**
   * If the user is analyst manager, he can access all modules. If not, the modules of the user are listed.
   */
  const userModules: ComputedRef<Recordable[]> = computed(() => {
    if (isAnyAnalystManager.value) return configs.modules || []
    return user.value?.modules || []
  })

  /**
   * @method setLoggedIn
   * @param value - boolean
   * @description Set the logged in status of the user.
   */
  function setLoggedIn(value: boolean) {
    isLoggedIn.value = value
  }

  /**
   * @method resetMFAActivationRequirements
   * @description Reset the MFA activation requirements.
   */
  function resetMFAActivationRequirements() {
    MFAActivationUID.value = ''
    MFAActivationToken.value = ''
    MFAActivationQRCodeURI.value = ''
  }

  /**
   * @method setLoginEmail
   * @param email - string
   * @description Set the login email.
   */
  function setLoginEmail(email: string) {
    loginEmail.value = email
  }

  /**
   * @method setRememberedEmail
   * @param email - string
   * @description Set the remembered email for persistent storage across sessions.
   */
  function setRememberedEmail(email: string) {
    rememberedEmail.value = email
  }

  /**
   * @method clearRememberedEmail
   * @description Clear the remembered email from persistent storage.
   */
  function clearRememberedEmail() {
    rememberedEmail.value = ''
  }

  /**
   * @method setLoginTypes
   * @param types - LoginTypesResponse | null
   * @description Set the login types.
   */
  function setLoginTypes(types: LoginTypesResponse | null) {
    loginTypes.value = types
  }

  /**
   * @method resetLoginEmail
   * @description Reset the login email and clear OTP countdown.
   */
  function resetLoginEmail() {
    if (loginEmail.value) {
      sessionStorage.removeItem(`otp-countdown-${loginEmail.value}`)
    }
    loginEmail.value = ''
    loginTypes.value = null
  }

  /**
   * @method setLanguage
   * @param language - string
   * @description Set the language of the user.
   */
  function setLanguage(language: string | undefined) {
    defHttp.setHeader({ [ACCEPT_LANGUAGE_KEY]: language })
    userLanguage.value = language
  }

  /**
   * @method setLoginProgress
   * @param value - boolean
   * @description Set the login progress.
   */
  function setLoginProgress(value: boolean) {
    isLoginProgressContinue.value = value
  }

  /**
   * @method resetLoginProgress
   * @description Reset the login progress.
   */
  function resetLoginProgress() {
    isLoginProgressContinue.value = false
  }

  /**
   * @method setImpersonatingOrReleasingProgress
   * @param progress - boolean
   * @description Set the impersonating or releasing progress.
   */
  function setImpersonatingOrReleasingProgress(progress: boolean) {
    isImpersonatingOrReleasingProgressContinue.value = progress
  }

  /**
   * @method setUser
   * @param newUser - AccountModel | undefined
   * @description Set the user.
   */
  function setUser(newUser: AccountModel | undefined) {
    user.value = newUser
    setLoggedIn(true)
  }

  /**
   * @method setLoading
   * @param newLoading - boolean
   * @description Set the loading status.
   */
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }

  /**
   * @method savePreviousSessionUser
   * @param userInfo - string
   * @description Save the previous session user.
   */
  function savePreviousSessionUser(userInfo: string) {
    previousSessionUser.value = userInfo
  }

  /**
   * @method getCsrfToken
   * @description Get the CSRF token.
   */
  async function getCsrfToken() {
    const response = await accountCsrfTokenApi()
    csrfToken.value = response.headers[CSRF_TOKEN_KEY]!
    token.value = csrfToken.value
  }

  /**
   * @method setThirdPartyIntegration
   * @param newUser - AccountModel
   * @description Set the third party integration.
   */
  function setThirdPartyIntegration(newUser: AccountModel) {
    // Analytics removed in prototype edition
    console.log('User identified:', newUser.email)
  }

  /**
   * @method clearThirdPartyIntegration
   * @description Clear the third party integrations.
   */
  function clearThirdPartyIntegration() {
    // Analytics removed in prototype edition
    console.log('User session cleared')
  }

  /**
   * @method getAccountInfo
   * @description Get the account info. This method fetches the current user's account information,
   * sets the user data in the store, retrieves a new CSRF token, configures third-party integrations,
   * updates the impersonation status, and sets the user's language preference.
   * @returns {Promise<AccountModel>} A promise that resolves to the user's account model
   */
  async function getAccountInfo(): Promise<AccountModel> {
    isLoginProgressContinue.value = false
    // Remember the email before resetting
    if (loginEmail.value) {
      setRememberedEmail(loginEmail.value)
    }
    resetLoginEmail()
    const newUser = await accountInfoApi()

    // Set the user data in the store.
    setUser(newUser)
    // Get a new CSRF token.
    await getCsrfToken()
    // Configure third-party integrations.
    setThirdPartyIntegration(newUser)
    // Update the impersonation status.
    setImpersonatingOrReleasingProgress(false)
    // Set the user's language preference.
    setLanguage(newUser?.organization?.main_language || 'en')

    return newUser
  }

  /**
   * @method setMFAActivationRequirements
   * @param params - MFAActivationRequirements
   * @description Set the MFA activation requirements.
   */
  function setMFAActivationRequirements(params: MFAActivationRequirements) {
    MFAActivationUID.value = encode(params.uid)
    MFAActivationToken.value = encode(params.token)
    MFAActivationQRCodeURI.value = encode(params.qr_code_uri)
  }

  /**
   * @method setAccountMFAPreferences
   * @param response - AccountModel
   * @description Set the MFA activation preferences.
   */
  function setAccountMFAPreferences(response: AccountModel) {
    isMFARequired.value = response.is_2fa_enabled
    isMFAActivationEnforced.value = response.force_2fa ?? false
  }

  /**
   * @method enableUnauthorizedAccountMFA
   * @param params - MFAActivationUnauhthorizedModel
   * @description Enable the unauthorized account MFA.
   */
  async function enableUnauthorizedAccountMFA(params: MFAActivationUnauhthorizedModel) {
    await unauthorizedAccountEnableMFAApi({
      mfa_token: params.mfa_token,
      password: params.password,
      uid: decode(MFAActivationUID.value),
      token: decode(MFAActivationToken.value),
    })
    resetMFAActivationRequirements()
    await getAccountInfo()
  }

  /**
   * @method login
   * @param params - LoginParams
   * @description Login the user.
   */
  async function login(params: LoginParams) {
    const response = await accountLoginApi(params)
    loginEmail.value = params.email

    isLoginProgressContinue.value = true
    setAccountMFAPreferences(response)

    // if MFA activation required then set requirements
    if (isMFAActivationEnforced.value) {
      setMFAActivationRequirements(response)
    } else if (!isMFARequired.value && !isMFAActivationEnforced.value) {
      await getAccountInfo()
    }
  }

  /**
   * @method sendPasswordResetLink
   * @param email - string
   * @description Send the password reset link.
   */
  async function sendPasswordResetLink(email: string) {
    await accountPasswordResetRequestApi(email)
  }

  /**
   * @method sendOTPTokenRequest
   * @param email - string
   * @description Send the OTP token request.
   */
  async function sendOTPTokenRequest(email: string) {
    loginEmail.value = email
    isLoginProgressContinue.value = true
    await accountSendOTPTokenRequestApi(email)
  }

  /**
   * @method resetPassword
   * @param params - PasswordReset
   * @description Reset the password.
   */
  async function resetPassword(params: PasswordReset) {
    await accountPasswordResetApi(params)
  }

  /**
   * @method changePassword
   * @param params - PasswordChange
   * @description Change the password.
   */
  async function changePassword(params: PasswordChange) {
    return accountPasswordChangeApi(params)
  }

  /**
   * @method loginWithOTP
   * @param OTPToken - OTPLoginParams
   * @description Login with OTP.
   */
  async function loginWithOTP(OTPToken: OTPLoginParams) {
    await accountLoginWithOTPApi(OTPToken)

    await getAccountInfo()
  }

  /**
   * @method loginWithMFA
   * @param otpCode - string
   * @description Login with MFA.
   */
  async function loginWithMFA(mfaToken: string) {
    await accountLoginWithMFAApi(mfaToken)
    await getAccountInfo()
  }

  /**
   * @method logout
   * @param withLogout - boolean
   * @description Logout the user. This method handles the complete logout process including:
   * - Sending logout request to server if user is logged in
   * - Refreshing CSRF token
   * - Clearing user data and third party integrations
   * - Removing organization context
   * - Redirecting to login page
   * @param withLogout - If true, sends logout request to server. Defaults to true.
   */
  async function logout(withLogout = true) {
    const { switchOrganization } = useOrganizationStore()
    // if loggedIn send logout request
    if (isLoggedIn.value && withLogout) {
      await accountLogoutApi()
      await getCsrfToken()
    }
    // Remove switched organization
    setLoggedIn(false)
    await nextTick()
    clearThirdPartyIntegration()
    user.value = undefined
    resetLoginEmail()
    switchOrganization(undefined)
    await router.push({ name: 'Root' })
    setLanguage(undefined)
  }

  return {
    // Account related variables
    user,
    loading,
    csrfToken,
    userModules,
    userLanguage,
    accountOrganization,
    isMultiLanguage,

    // Authentication state variables
    isLoggedIn,
    loginEmail,
    rememberedEmail,
    loginTypes,
    isImpersonated,
    isLoginProgressContinue,
    isImpersonatingOrReleasingProgressContinue,

    // MFA related variables
    isMFARequired,
    MFAActivationUID,
    MFAActivationToken,
    previousSessionUser,
    MFAActivationQRCodeURI,
    isMFAActivationEnforced,

    // Account related functions
    setUser,
    setLoading,
    setLanguage,
    getCsrfToken,
    resetPassword,
    changePassword,
    getAccountInfo,
    clearThirdPartyIntegration,

    // Authentication functions
    login,
    logout,
    setLoggedIn,
    loginWithMFA,
    loginWithOTP,
    setLoginEmail,
    resetLoginEmail,
    setRememberedEmail,
    clearRememberedEmail,
    setLoginTypes,
    logoutUser: logout,
    setLoginProgress,
    resetLoginProgress,
    sendOTPTokenRequest,
    sendPasswordResetLink,

    // MFA related functions
    savePreviousSessionUser,
    setAccountMFAPreferences,
    setMFAActivationRequirements,
    enableUnauthorizedAccountMFA,
    resetMFAActivationRequirements,
    setImpersonatingOrReleasingProgress,
  } as const
})

// Need to be used outside the setup
export function useAccountStoreWithout() {
  return useAccountStore(store)
}

/**
 * Pinia supports Hot Module replacement, so you can edit your store and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAccountStore, import.meta.hot))
}
